import { ArrowUpRight, Clock, Monitor, ShieldCheck, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card } from '@/components/ui';
import { useAllSessions } from '@/hooks/useAdminSessions';
import { usePendingUsers, useUsers } from '@/hooks/useUsers';
import cn from '@/utils/cn';
import { ROUTES } from '@/utils/constants';

const AdminOverviewPage = () => {
  // limit: 1 - we only need meta.total here, the full table lives on Admin > Users.
  const { data: usersPage } = useUsers({ page: 1, limit: 1 });
  const { data: pending } = usePendingUsers();
  const { data: sessions } = useAllSessions();

  const stats = [
    {
      label: 'Total users',
      value: usersPage?.total ?? '-',
      caption: 'View directory',
      icon: Users,
      to: ROUTES.ADMIN_USERS,
      chip: 'border-brand/20 bg-brand-tint text-brand',
      hoverBorder: 'hover:border-brand/40',
    },
    {
      label: 'Pending approvals',
      value: pending?.length ?? '-',
      caption: 'Review requests',
      icon: ShieldCheck,
      to: ROUTES.APPROVALS,
      chip: 'border-warning/40 bg-warning-tint text-ink',
      hoverBorder: 'hover:border-warning/50',
    },
    {
      label: 'Active sessions',
      value: sessions?.length ?? '-',
      caption: 'See devices',
      icon: Monitor,
      to: ROUTES.ADMIN_SESSIONS,
      chip: 'border-success/20 bg-success-tint text-success',
      hoverBorder: 'hover:border-success/40',
    },
  ];

  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
        Console overview
      </h1>
      <p className="mt-1.5 text-sm text-ink/60">
        A snapshot of the workspace: who has access, who&apos;s waiting, and who&apos;s online.
      </p>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {stats.map(({ label, value, caption, icon: Icon, to, chip, hoverBorder }) => (
          <Link key={label} to={to} className="group">
            <Card
              className={cn(
                'h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg',
                hoverBorder,
              )}
            >
              <div className="flex items-start justify-between">
                <div className={cn('grid h-11 w-11 place-items-center rounded-xl border', chip)}>
                  <Icon className="h-5 w-5" />
                </div>
                <ArrowUpRight className="h-4 w-4 text-ink/25 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand" />
              </div>
              <p className="mt-5 text-xs font-bold uppercase tracking-wider text-ink/40">{label}</p>
              <p className="mt-1 text-3xl font-extrabold text-ink">{value}</p>
              <p className="mt-3 text-xs font-semibold text-ink/45 transition-colors group-hover:text-brand">
                {caption}
              </p>
            </Card>
          </Link>
        ))}
      </div>

      {!!pending?.length && (
        <Card
          padding="none"
          className="mt-6 flex flex-wrap items-center justify-between gap-4 border-warning/40 bg-warning-tint p-5"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/70 text-warning">
              <Clock className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-bold text-ink">
                {pending.length} registration
                {pending.length === 1 ? '' : 's'} awaiting your review
              </p>
              <p className="text-sm text-ink/60">
                Review and approve or reject new access requests.
              </p>
            </div>
          </div>
          <Link
            to={ROUTES.APPROVALS}
            className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-ink/85"
          >
            Review now
          </Link>
        </Card>
      )}
    </div>
  );
};

export default AdminOverviewPage;
